import {
  AssociateAssessmentToGroupDTO,
  GroupAssessmentDTO,
  GroupCreationDTO,
  GroupSummary,
  mapToCreationGroupDTO,
  mapToGroupAssessment,
} from '$synapse/dtos';
import { Group, GroupAssessment, Page, PageRequest } from '$synapse/models';
import {
  catchError,
  map,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  toArray,
} from 'rxjs';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { genericBaseUrl } from './utils';

@Injectable({
  providedIn: 'root',
})
export class GroupService {
  private readonly _http = inject(HttpClient);
  private readonly _toastr = inject(ToastrService);

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  private _baseUrl(...path: any[]): string {
    return genericBaseUrl(
      `${environment.settings.api.url}/manager/groups`,
      ...path
    );
  }

  getGroup(groupId: string): Observable<Group> {
    return this._http
      .get<Group>(this._baseUrl(groupId))
      .pipe(this.toastError());
  }

  findAllGroups(): Observable<Group[]> {
    return this._http.get<Group[]>(this._baseUrl('filterList'));
  }

  findAll(page: PageRequest): Observable<Page<Group>> {
    return this._http
      .get<Page<Group>>(this._baseUrl(), {
        params: page.params(),
      })
      .pipe(
        map((response) => {
          return new Page(page, response.content, response.totalPages);
        }),
        this.toastError()
      );
  }

  findAllAssessmentsByGroupId(
    groupId: string
  ): Observable<Array<GroupAssessment>> {
    return this._http
      .get<Array<GroupAssessmentDTO>>(this._baseUrl(groupId, 'assessments'))
      .pipe(
        mergeMap((elt) => elt),
        map(mapToGroupAssessment),
        toArray()
      );
  }

  findAllAssessmentsPageByGroupId(
    groupId: string,
    page: PageRequest
  ): Observable<Page<GroupAssessment>> {
    return this._http
      .get<Page<GroupAssessmentDTO>>(
        `${this._baseUrl(groupId, 'assessments')}`,
        {
          params: page.params(),
        }
      )
      .pipe(
        map((response) => {
          const content = response.content.map(mapToGroupAssessment);
          page = page.setDefaultSort('name,desc');
          return new Page(page, content, content.length);
        })
      );
  }

  getAll(page: PageRequest): Observable<Page<Group>> {
    return this._http
      .get<Page<Group>>(this._baseUrl(), {
        params: page.withNolimit().params(),
      })
      .pipe(
        map((response) => {
          return new Page(page, response.content, response.content.length);
        }),
        this.toastError()
      );
  }

  create(group: GroupCreationDTO): Observable<Group> {
    return this._http
      .post<Group>(this._baseUrl(), group, this.httpOptions)
      .pipe(
        map(() => {
          this._toastr.success(`Groupe créé avec succès`);
          return true;
        }),
        this.toastError()
      );
  }

  update(group: Group): Observable<Group> {
    return this._http
      .put<Group>(
        this._baseUrl(group.id),
        mapToCreationGroupDTO(group),
        this.httpOptions
      )
      .pipe(
        map((data) => {
          this._toastr.success(`Groupe modifié avec succès`);
          return data;
        }),
        this.toastError()
      );
  }

  private toastError(): OperatorFunction<any, any> {
    return catchError((err: HttpErrorResponse) => {
      this._toastr.error(err.error.message, 'Une erreur est survenue');
      return of(null);
    });
  }

  assignAssessmentToGroup(
    group: Group,
    assessment: AssociateAssessmentToGroupDTO
  ) {
    return this._http
      .post(this._baseUrl(group.id, 'assessments'), assessment, {
        observe: 'response',
      })
      .pipe(
        map(() => {
          this._toastr.success(
            `Un email a été envoyé aux candidats du groupe ${group.name}`,
            'Evaluations assignées avec succès'
          );
          return true;
        }),
        this.toastError()
      );
  }

  importGroupStatsToCsv(groupId: string): Observable<any> {
    return this._http
      .post(this._baseUrl(groupId, 'stats'), {}, { responseType: 'text' })
      .pipe(
        map((data) => {
          this._toastr.success('Téléchargement des statistiques des candidats');
          return data;
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.error) {
            this._toastr.error(err.error.message, 'Une erreur est survenue');
          }
          return of(null);
        })
      );
  }

  removeCandidatesFromGroup(
    group: Group,
    candidateIds: string[]
  ): Observable<any> {
    return this._http
      .post(this._baseUrl(group.id, 'candidates/remove'), candidateIds, {
        observe: 'response',
      })
      .pipe(
        map(() => {
          this._toastr.success('Candidat(s) dissocié(s) du groupe avec succès');
          return true;
        }),
        this.toastError()
      );
  }

  addCandidatesToGroup(
    group: Group | GroupSummary,
    candidates: string[]
  ): Observable<any> {
    return this._http
      .post(this._baseUrl(group.id, 'candidates/add'), candidates, {
        observe: 'response',
      })
      .pipe(
        map(() => {
          this._toastr.success(
            'Candidat(s) ajouté(s) au groupe ' + group.name + ' avec succès'
          );
          return true;
        }),
        catchError((err: HttpErrorResponse) => {
          const errorMessage = err.error
            ? err.error.message
            : 'Le candidat existe déjà dans ce groupe';
          this._toastr.error(errorMessage, 'Une erreur est survenue');
          return of(false);
        })
      );
  }

  deleteGroup(groupId: string): Observable<any> {
    return this._http.delete(this._baseUrl(groupId)).pipe(
      map(() => {
        this._toastr.success('Groupe supprimé avec succès');
        return true;
      }),
      this.toastError()
    );
  }

  deleteGroupAssessments(
    groupId: string,
    groupAssessmentsIds: number[]
  ): Observable<any> {
    return this._http
      .delete(this._baseUrl(groupId, 'assessments'), {
        body: groupAssessmentsIds,
      })
      .pipe(
        map(() => {
          this._toastr.success(
            'Évaluations supprimée(s) du groupe avec succès'
          );
          return true;
        }),
        this.toastError()
      );
  }

  sendEmailRemindCandidatesOfGroup(groupId: string, candidatesIds?: string[]) {
    this._toastr.info('Envoi des emails en cours');
    return this._http
      .post(
        this._baseUrl(groupId, 'candidates', 'remind'),
        candidatesIds ?? null
      )
      .pipe(
        map(() => {
          this._toastr.clear();
          this._toastr.success('Mails de relances envoyés avec succès');
          return true;
        }),
        catchError((err) => {
          this._toastr.clear();
          if (err.error) {
            this._toastr.error(err.error.message, 'Une erreur est survenue');
          } else {
            this._toastr.error('Une erreur est survenue');
          }
          return of(false);
        })
      );
  }

  countReminderEmails(groupId: string): Observable<any> {
    return this._http
      .get<number>(this._baseUrl(groupId, 'candidates', 'remind'), {})
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this._toastr.error(
            err.error.message,
            'Une erreur est survenue pour le calcul de nombre des emails'
          );
          return of(false);
        })
      );
  }

  updateGroupAssessmentExpirationDate(
    groupId: string,
    groupAssessmentId: string,
    newExpirationDate: Date
  ): Observable<GroupAssessmentDTO> {
    return this._http
      .patch(
        this._baseUrl(groupId, 'assessments/expiration-date'),
        {
          updatedExpirationDate: newExpirationDate.toISOString(),
          groupAssessmentId: groupAssessmentId,
        },
        this.httpOptions
      )
      .pipe(
        map((data) => {
          this._toastr.success(`Date changée avec succès`);
          return data;
        }),
        this.toastError()
      );
  }
}
